/*
 * File: useOneClickRedirect.ts
 * Project: meki
 * File Created: Monday, 11th January 2021 2:59:54 pm
 * Author: Gabriel Ulloa (gabriel@inventures.cl)
 * -----
 * Last Modified: Friday, 11th November 2022 12:46:04 pm
 * Modified By: Blanca Munizaga (blanca@inventures.cl)
 * -----
 * Copyright 2019 - 2021 Incrementa Ventures SpA. ALL RIGHTS RESERVED
 * Terms and conditions defined in license.txt
 * -----
 * Inventures - www.inventures.cl
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, makeReference } from '@apollo/client';
import {
  finishOneClickInscription,
  FinishOneClickInscriptionParams,
  FinishOneClickInscriptionResponse,
  getOneClickRedirect,
  GetOneClickRedirectParams,
  GetOneClickRedirectResponse,
} from '@queries/payment';
import { redirect } from '@utils/redirect';
import { useRouter } from 'next/router';
import { useAuth } from '@hooks/useAuth';
import { useOrderState } from '@hooks/useOrderState';
import { useToast } from '@hooks/toast.hook';
import { PaymentMethod } from '@interfaces';
import { useSegment } from '@hooks/useSegment';

/* Este hook se encarga de manejar todas las redirecciones de oneClick 
   y las interacciones con nuestro back-end para confirmar la inscripción de una tarjeta. */
export function useOneClickRedirect(
  urlToRedirect = '/pago/metodos',
  showError = (): void => {},
  handleIsRegistryError = (): void => {},
): [() => void, { loading: boolean }] {
  // Hooks init
  const apolloClient = useApolloClient();
  const { query, replace } = useRouter();
  const router = useRouter();
  const toasts = useToast();
  const { track, flush } = useSegment();
  // App state
  const { orderUuid, order } = useOrderState();
  const { appUser: user } = useAuth();

  // Local State
  const [loading, setLoading] = useState(false);

  // Other vars
  const finishedInscriptionRef = useRef(false);
  const [finishOneClickMutation] = useMutation<
    FinishOneClickInscriptionResponse,
    FinishOneClickInscriptionParams
  >(finishOneClickInscription('finishOneClickPago'), {
    update(cache, { data: { finishOneClickInscription } }) {
      cache.modify({
        id: cache.identify(makeReference('ROOT_QUERY')),
        fields: {
          paymentMethods(currentPaymentMethods: PaymentMethod[]) {
            return [finishOneClickInscription, ...currentPaymentMethods];
          },
        },
      });
      if (order)
        cache.modify({
          id: cache.identify({ __ref: 'Order:' + String(order.id) }),
          fields: {
            paymentMethod() {
              return finishOneClickInscription;
            },
          },
        });
    },
  });

  // Effects

  /**
   * Handle one click return
   */
  useEffect(() => {
    if (!query.TBK_TOKEN || !user || finishedInscriptionRef.current) return;
    finishedInscriptionRef.current = true;
    setLoading(true);
    finishOneClickMutation({
      variables: {
        params: {
          token: query.TBK_TOKEN as string,
          orderUuid: orderUuid,
        },
      },
    })
      .then((response) => {
        void track('add payment succeed', {
          payment_method_type: response.data.finishOneClickInscription.type,
          userId: user?.id,
        });
      })
      .catch(() => {
        handleIsRegistryError();
        if (showError) return showError();
        toasts.showToast({
          message: 'Ocurrio un error en la inscripción del medio de pago',
          type: 'error',
        });
      })
      .finally(() => {
        void router.replace({
          query: { ...router.query, TBK_TOKEN: null },
        });
        setLoading(false);
      });
  }, [
    router,
    query.TBK_TOKEN,
    replace,
    finishOneClickMutation,
    orderUuid,
    user,
    toasts,
    showError,
    handleIsRegistryError,
    track,
  ]);

  // Logic

  const getOneClickRedirectCallback = useCallback(async () => {
    const {
      data: {
        getRedirectUrlOneClick: { url, token, method },
      },
    } = await apolloClient.query<
      GetOneClickRedirectResponse,
      GetOneClickRedirectParams
    >({
      query: getOneClickRedirect('paymentSelectOneClick'),
      variables: {
        params: {
          urlReturn:
            window.location.origin +
            `/api/oneClick?redirectPath=${urlToRedirect}`,
        },
      },
      fetchPolicy: 'network-only',
    });
    return { url, token, method };
  }, [apolloClient, urlToRedirect]);

  const redirectCallback = useCallback(async () => {
    void track('add payment start', {
      payment_method_type: 'ONE_CLICK',
      userId: user?.id,
    });
    flush();
    const { url, method, token } = await getOneClickRedirectCallback();
    redirect({ url, method, params: { TBK_TOKEN: token } });
  }, [getOneClickRedirectCallback, track, flush]);

  return [redirectCallback, { loading }];
}
